import React, { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import {
  ArrowRightIcon, ArrowTopRightOnSquareIcon, BuildingOffice2Icon, CheckIcon, CreditCardIcon,
  EnvelopeIcon, ExclamationCircleIcon, EyeIcon, EyeSlashIcon, LockClosedIcon, ShieldCheckIcon, UserIcon,
} from '@heroicons/react/24/outline'
import * as konto from '../api/account'
import { signup, errorText, hasPendingFlow } from '../api/auth'
import { useAuth } from '../context/AuthContext'
import useDocumentTitle from '../hooks/useDocumentTitle'
import { zdarzenie } from '../lib/pomiar'
import { zapiszPowrot } from '../lib/powrotPoRejestracji'
import { EMAIL_RE, ocenHaslo } from '../lib/ocenaHasla'
import { KORZYSCI_FIRMY, KORZYSCI_KONTA } from '../components/auth/korzysciKonta'
import { TEKST_FIRMA_MA_KONTO } from '../config/rejestracjaFirmy'

/**
 * Rejestracja `/rejestracja`, nowy wygląd z makiety „Rejestracja — nowy wygląd” (artefakt Design, plansze Main,
 * Firma, Skrzynka i Telefon; prośba właściciela 2026-10-13). Dane i API bez zmian względem poprzedniej wersji:
 * e-mail, hasło, zgody oraz OPCJONALNY profil (rodzaj konta, czym się zajmujesz, firma z NIP), wysyłane razem
 * z `signup`, bo przed potwierdzeniem adresu nie ma sesji.
 *
 * Co się zmieniło: jedno pole hasła z podglądem i miernikiem siły zamiast dwóch pól (te same reguły co walidatory
 * Django, więc błąd widać przed wysłaniem); profil od razu widoczny, ale nadal opcjonalny; NIP szuka się sam po
 * wpisaniu 10 cyfr z poprawną cyfrą kontrolną; po wysłaniu ekran „Sprawdź swoją skrzynkę” z krokami i skrótami
 * do poczty. Przycisku „Wyślij link ponownie” z makiety nie ma: allauth w trybie linku nie ma takiego endpointu.
 */

const PUSTA_FIRMA = { nip: '', name: '', street: '', postal_code: '', city: '', regon: '' }

const SILA = [
  { pasek: 'bg-gray-200', tekst: 'text-gray-500', nazwa: '' },
  { pasek: 'bg-red-600', tekst: 'text-red-700', nazwa: 'Słabe' },
  { pasek: 'bg-amber-500', tekst: 'text-amber-700', nazwa: 'Średnie' },
  { pasek: 'bg-blue-600', tekst: 'text-blue-700', nazwa: 'Dobre' },
  { pasek: 'bg-emerald-600', tekst: 'text-emerald-700', nazwa: 'Mocne' },
]

const nipPoprawny = (cyfry) => {
  if (!/^\d{10}$/.test(cyfry)) return false
  const wagi = [6, 5, 7, 2, 3, 4, 5, 6, 7]
  const suma = wagi.reduce((s, w, i) => s + w * Number(cyfry[i]), 0)
  return suma % 11 === Number(cyfry[9])
}


const poleKlasa = (blad) =>
  `h-[50px] w-full rounded-xl border-[1.5px] bg-white text-[15px] text-gray-900 placeholder:text-gray-400 transition
   focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-600/15 disabled:bg-gray-50
   ${blad ? 'border-red-600' : 'border-gray-300'}`
const malePoleKlasa =
  'h-11 w-full rounded-[10px] border-[1.5px] border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-600/15'

const Chip = ({ on, onClick, title, children }) => (
  <button type="button" aria-pressed={on} onClick={onClick} title={title}
          className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-full border-[1.5px] px-3.5 text-[13.5px] transition
                      ${on ? 'border-blue-600 bg-blue-600 font-semibold text-white' : 'border-gray-300 bg-white font-medium text-gray-700 hover:border-gray-400'}`}>
    {on && <CheckIcon className="h-3.5 w-3.5" strokeWidth={2.6} aria-hidden="true" />}
    {children}
  </button>
)

const Blad = ({ children }) => (
  <span role="alert" className="flex items-center gap-1.5 text-[13px] text-red-700">
    <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />{children}
  </span>
)

const Signup = () => {
  useDocumentTitle('Załóż konto')
  const { isAuthenticated, loading, profil, profilUstalany } = useAuth()
  // Wejście z linku typu /rejestracja?email=... (np. po zgłoszeniu jako wykonawca).
  const [searchParams] = useSearchParams()
  // Skąd przyszła osoba (bramka, okno limitu): po potwierdzeniu e-maila i logowaniu wraca w to samo miejsce.
  const location = useLocation()

  const [email, setEmail] = useState(searchParams.get('email') || '')
  const [haslo, setHaslo] = useState('')
  const [pokaz, setPokaz] = useState(false)
  const [regulamin, setRegulamin] = useState(false)
  const [marketing, setMarketing] = useState(false)
  // Część OPCJONALNA: nic z niej nie jest potrzebne do założenia konta.
  // `?konto=firma` (przyciski na /dla-firm, 2026-10-22): rejestracja startuje od razu z wybraną firmą i polem NIP.
  const [rodzaj, setRodzaj] = useState(searchParams.get('konto') === 'firma' ? 'company' : '')
  const [typy, setTypy] = useState([])
  const [ostatniTyp, setOstatniTyp] = useState('')
  const [inne, setInne] = useState('')
  const [firma, setFirma] = useState(PUSTA_FIRMA)
  const [nipStan, setNipStan] = useState('') // '', szukam, zly, znaleziony, brak
  const [nipKomunikat, setNipKomunikat] = useState('')
  const [maKonto, setMaKonto] = useState(false)
  const [vat, setVat] = useState('')
  const [edycja, setEdycja] = useState(false)

  const [proba, setProba] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const zapytanie = useRef(0)
  useEffect(() => () => { zapytanie.current += 1 }, [])

  // Zalogowany: z „Dla firm PV” (`?konto=firma`) do podpięcia NIP-u albo na pulpit firmy, a nie na stronę główną,
  // gdzie przycisk „Zarejestruj się” zostawiał konto prywatne bez wskazówki (2026-10-24).
  if (!loading && isAuthenticated) {
    if (searchParams.get('konto') !== 'firma') return <Navigate to="/" replace />
    if (profilUstalany) return null
    return <Navigate to={profil?.company ? '/pulpit' : '/konto?sekcja=firma'} replace />
  }

  const emailCzysty = email.trim()
  const emailOk = EMAIL_RE.test(emailCzysty)
  const ocena = ocenHaslo(haslo, emailCzysty)
  const firmowe = rodzaj === 'company'
  const prywatne = rodzaj === 'private'
  // Konto firmowe tylko z NIP-em (decyzja właściciela 2026-10-24): bez niego konto prywatne. Gdy rejestr nie zna
  // NIP-u albo nie odpowiada, potrzebna też nazwa, bo bez niej firmy nie da się założyć po potwierdzeniu adresu.
  const brakNipu = firmowe && !nipPoprawny(firma.nip.replace(/\D/g, ''))
  const brakNazwy = firmowe && nipStan === 'brak' && !firma.name.trim()

  // Zmiana rodzaju konta czyści odpowiedzi: „mam instalację PV” nic nie znaczy w profilu hurtowni.
  const wybierzRodzaj = (r) => {
    setRodzaj((obecny) => (obecny === r ? '' : r))
    setTypy([]); setOstatniTyp(''); setInne('')
  }
  const przelaczTyp = (value) => {
    if (prywatne) { setTypy((t) => (t[0] === value ? [] : [value])); return }
    setTypy((t) => (t.includes(value) ? t.filter((v) => v !== value) : [...t, value]))
    setOstatniTyp((o) => (typy.includes(value) ? (o === value ? '' : o) : value))
  }

  // NIP szuka się sam po 10 cyfrach z poprawną cyfrą kontrolną. Brak odpowiedzi rejestru nie blokuje
  // rejestracji: wtedy pola firmy wpisuje się ręcznie.
  const zmienNip = async (wartosc) => {
    const cyfry = wartosc.replace(/\D/g, '')
    setFirma((f) => ({ ...f, nip: wartosc }))
    setVat(''); setNipKomunikat(''); setMaKonto(false)
    const nr = ++zapytanie.current
    if (cyfry.length < 10) { setNipStan(''); return }
    if (!nipPoprawny(cyfry)) { setNipStan('zly'); return }
    setNipStan('szukam'); setEdycja(false)
    const res = await konto.lookupNip(cyfry)
    if (nr !== zapytanie.current) return
    setMaKonto(Boolean(res.ok && res.data.ma_konto))
    if (res.ok && res.data.status === 'found') {
      const d = res.data
      setFirma({ nip: d.nip || wartosc, name: d.name || '', street: d.street || '', postal_code: d.postal_code || '', city: d.city || '', regon: d.regon || '' })
      setVat(d.vat_status || '')
      setNipStan('znaleziony')
      return
    }
    setNipStan('brak'); setEdycja(true)
    setNipKomunikat(res.ok ? (res.data.message || 'Nie znaleźliśmy tego NIP w rejestrze. Wpisz dane firmy ręcznie.') : res.error)
  }

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    setProba(true)
    if (!emailOk || !ocena.ok || !regulamin || brakNipu || brakNazwy) return

    setBusy(true)
    const res = await signup({
      email: emailCzysty,
      password: haslo,
      acceptTerms: regulamin,
      marketingConsent: marketing,
      userTypes: typy,
      userTypeOther: typy.includes('other') ? inne : '',
      accountType: rodzaj,
      // NIP tylko przy koncie firmowym, żeby porzucony wybór nie zakładał konta z firmą.
      company: firmowe && firma.nip.trim() ? firma : null,
    })
    setBusy(false)

    // allauth zwraca HTTP 401 przy UDANEJ rejestracji, gdy adres czeka na potwierdzenie; rozstrzyga `verify_email`.
    if (hasPendingFlow(res, 'verify_email') || res.ok || res.meta.is_authenticated) {
      zdarzenie('rejestracja', firmowe ? 'firmowe' : 'prywatne')
      zapiszPowrot(location.state?.from)
      setDone(true)
      window.scrollTo(0, 0)
      return
    }
    setError(errorText(res, 'Nie udało się założyć konta.'))
  }

  if (done) {
    return (
      <Skrzynka email={emailCzysty} firma={firmowe && firma.nip.trim() ? firma : null}
                maKonto={maKonto} onPopraw={() => { setDone(false); setProba(false) }} />
    )
  }

  const typyKonta = konto.typyDlaKonta(rodzaj)
  const ostatni = typyKonta.find((t) => t.value === ostatniTyp)
  const opisTypu = (v) => konto.USER_TYPE_DESCRIPTIONS[v] || ''
  const wybranyPrywatny = prywatne && typy[0]
  const adresFirmy = [firma.street, [firma.postal_code, firma.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')

  return (
    <div className="bg-gray-50 text-gray-900">
      <div className="mx-auto flex max-w-[1160px] flex-col gap-10 px-4 pb-12 pt-8 sm:px-6 sm:pt-12 lg:flex-row-reverse lg:items-start lg:gap-16">

        <section aria-labelledby="tytul-formularza"
                 className="w-full min-w-0 rounded-[20px] border border-gray-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_32px_rgba(15,23,42,0.06)] sm:p-8 lg:max-w-[540px] lg:flex-[1_1_460px]">
          <form onSubmit={submit} noValidate className="flex flex-col gap-[22px]">
            <div className="flex flex-col gap-1">
              <h2 id="tytul-formularza" className="m-0 text-2xl font-bold">Załóż konto</h2>
              <p className="m-0 text-sm text-gray-500">
                {firmowe ? 'E-mail, hasło i NIP firmy. Nazwę i adres firmy pobierzemy sami.' : 'Wystarczą e-mail i hasło. Resztę uzupełnisz, kiedy zechcesz.'}
              </p>
            </div>

            {error && (
              <p role="alert" className="m-0 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />{error}
              </p>
            )}

            <div className="flex flex-col gap-1.5">
              <label htmlFor="rej-email" className="text-sm font-semibold">Adres e-mail</label>
              <div className="relative">
                <EnvelopeIcon className="pointer-events-none absolute left-3.5 top-[15px] h-5 w-5 text-gray-400" aria-hidden="true" />
                <input id="rej-email" type="email" autoComplete="email" placeholder="jan@firma.pl" value={email}
                       onChange={(e) => setEmail(e.target.value)} disabled={busy}
                       aria-invalid={proba && !emailOk} className={`${poleKlasa(proba && !emailOk)} px-11`} />
                {emailOk && <CheckIcon className="pointer-events-none absolute right-3.5 top-[15px] h-5 w-5 text-emerald-700" strokeWidth={2.4} aria-hidden="true" />}
              </div>
              {proba && !emailOk && <Blad>{emailCzysty ? 'Ten adres wygląda na niepełny, np. jan@firma.pl.' : 'Wpisz adres e-mail.'}</Blad>}
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="rej-haslo" className="text-sm font-semibold">Hasło</label>
              <div className="relative">
                <LockClosedIcon className="pointer-events-none absolute left-3.5 top-[15px] h-5 w-5 text-gray-400" aria-hidden="true" />
                <input id="rej-haslo" type={pokaz ? 'text' : 'password'} autoComplete="new-password" placeholder="Co najmniej 8 znaków"
                       value={haslo} onChange={(e) => setHaslo(e.target.value)} disabled={busy}
                       aria-invalid={proba && !ocena.ok} className={`${poleKlasa(proba && !ocena.ok)} pl-11 pr-[52px]`} />
                <button type="button" onClick={() => setPokaz((p) => !p)} aria-label={pokaz ? 'Ukryj hasło' : 'Pokaż hasło'} aria-pressed={pokaz}
                        className="absolute right-[3px] top-[3px] flex h-11 w-11 items-center justify-center rounded-[10px] text-gray-600 hover:bg-gray-100">
                  {pokaz ? <EyeSlashIcon className="h-5 w-5" aria-hidden="true" /> : <EyeIcon className="h-5 w-5" aria-hidden="true" />}
                </button>
              </div>
              <div className="flex items-center gap-3">
                <div aria-hidden="true" className="grid flex-1 grid-cols-4 gap-1.5">
                  {[0, 1, 2, 3].map((i) => (
                    <span key={i} className={`h-1.5 rounded-full transition-colors ${i < ocena.wynik ? SILA[ocena.wynik].pasek : 'bg-gray-200'}`} />
                  ))}
                </div>
                <span aria-live="polite" className={`min-w-[92px] text-right text-[12.5px] font-semibold ${SILA[ocena.popularne ? 1 : ocena.wynik].tekst}`}>
                  {ocena.popularne ? 'Zbyt popularne' : SILA[ocena.wynik].nazwa}
                </span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[12.5px]">
                {ocena.reguly.map(([nazwa, ok]) => (
                  <span key={nazwa} className={`inline-flex items-center gap-1 ${ok ? 'font-semibold text-emerald-700' : 'text-gray-500'}`}>
                    {ok
                      ? <CheckIcon className="h-3.5 w-3.5" strokeWidth={2.6} aria-hidden="true" />
                      : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="7" /></svg>}
                    {nazwa}
                  </span>
                ))}
              </div>
              {proba && !ocena.ok && <Blad>{ocena.blad}</Blad>}
            </div>

            <div role="group" aria-labelledby="rej-typ-konta" className="flex flex-col gap-2.5 border-t border-gray-100 pt-[18px]">
              <span id="rej-typ-konta" className="text-sm font-semibold">Rejestrujesz się jako <span className="font-normal text-gray-500">(opcjonalnie)</span></span>
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {[['private', 'Osoba prywatna', 'Kupujesz dla siebie', UserIcon], ['company', 'Firma', 'Faktura, dane firmowe', BuildingOffice2Icon]].map(([wartosc, nazwa, opis, Icon]) => {
                  const on = rodzaj === wartosc
                  return (
                    <button key={wartosc} type="button" aria-pressed={on} onClick={() => wybierzRodzaj(wartosc)}
                            className={`flex min-h-[64px] items-center gap-3 rounded-[14px] border-[1.5px] px-3.5 py-3 text-left transition
                                        ${on ? 'border-blue-600 bg-blue-50 shadow-[0_0_0_3px_rgba(37,99,235,0.10)]' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                      <span className={`flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-[10px] ${on ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
                        <Icon className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <span className="flex min-w-0 flex-col">
                        <span className="text-[14.5px] font-semibold">{nazwa}</span>
                        <span className="text-[12.5px] text-gray-500">{opis}</span>
                      </span>
                    </button>
                  )
                })}
              </div>

              {prywatne && (
                <div className="flex flex-col gap-2.5 rounded-[14px] border border-gray-200 bg-gray-50 p-4">
                  <span className="text-[13.5px] font-semibold">Masz już instalację fotowoltaiczną?</span>
                  <div className="flex flex-wrap gap-2">
                    {typyKonta.map((t) => (
                      <Chip key={t.value} on={typy.includes(t.value)} onClick={() => przelaczTyp(t.value)} title={opisTypu(t.value)}>{t.label}</Chip>
                    ))}
                  </div>
                  <span className="text-[12.5px] text-gray-500">
                    {wybranyPrywatny ? `${opisTypu(wybranyPrywatny)}.` : 'Dzięki temu podpowiedzi i ceny lepiej trafią w Twoją sytuację.'}
                  </span>
                </div>
              )}

              {firmowe && (
                <div className="flex flex-col gap-4 rounded-[14px] border border-gray-200 bg-gray-50 p-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="rej-nip" className="text-[13.5px] font-semibold">NIP firmy<span className="text-red-700"> *</span></label>
                    <div className="relative">
                      <input id="rej-nip" inputMode="numeric" autoComplete="off" placeholder="np. 123-456-32-18" value={firma.nip}
                             onChange={(e) => zmienNip(e.target.value)} disabled={busy} aria-invalid={proba && brakNipu}
                             className={`h-12 w-full rounded-xl border-[1.5px] bg-white pl-3.5 pr-[84px] text-[15px] tracking-[0.02em] focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-600/15 ${nipStan === 'zly' || (proba && brakNipu) ? 'border-red-600' : 'border-gray-300'}`} />
                      {nipStan === 'szukam' && <span aria-hidden="true" className="absolute right-3.5 top-3.5 text-[12.5px] font-semibold text-blue-700">Szukam…</span>}
                      {nipStan === 'znaleziony' && <CheckIcon className="absolute right-3.5 top-3.5 h-5 w-5 text-emerald-700" strokeWidth={2.4} aria-hidden="true" />}
                    </div>
                    <span aria-live="polite" className={`text-[12.5px] ${maKonto ? 'font-semibold text-amber-800' : ({ zly: 'text-red-700', szukam: 'text-blue-700', znaleziony: 'text-emerald-700' }[nipStan] || 'text-gray-500')}`}>
                      {maKonto ? TEKST_FIRMA_MA_KONTO : {
                        '': 'Wymagany przy koncie firmowym. Nazwę i adres pobierzemy z Białej listy podatników VAT, gdy wpiszesz 10 cyfr.',
                        szukam: 'Szukam w Białej liście podatników VAT…',
                        zly: 'Ten NIP ma błędną cyfrę kontrolną. Sprawdź, czy nie ma literówki.',
                        znaleziony: 'Dane z Białej listy VAT. Sprawdzimy je jeszcze raz po potwierdzeniu adresu e-mail.',
                        brak: nipKomunikat,
                      }[nipStan]}
                    </span>
                    {proba && brakNipu && nipStan !== 'zly' && (
                      <Blad>Podaj NIP firmy. Bez NIP-u wybierz „Osoba prywatna”, a NIP dodasz później w ustawieniach konta.</Blad>
                    )}
                  </div>

                  {(nipStan === 'znaleziony' || nipStan === 'brak') && (
                    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3.5">
                      {nipStan === 'znaleziony' && (
                        <div className="flex items-start gap-3">
                          <span aria-hidden="true" className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-[10px] bg-blue-50 text-blue-600">
                            <BuildingOffice2Icon className="h-5 w-5" />
                          </span>
                          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="text-[14.5px] font-semibold">{firma.name || 'Firma bez nazwy w rejestrze'}</span>
                            {adresFirmy && <span className="text-[13px] text-gray-600">{adresFirmy}</span>}
                            {vat && (
                              <span className="mt-1.5 inline-flex items-center gap-1.5 self-start rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                                <CheckIcon className="h-3 w-3" strokeWidth={2.6} aria-hidden="true" />{vat === 'Czynny' ? 'Czynny podatnik VAT' : `Status VAT: ${vat}`}
                              </span>
                            )}
                          </div>
                          <button type="button" onClick={() => setEdycja((x) => !x)} aria-expanded={edycja}
                                  className="h-11 flex-shrink-0 rounded-[10px] px-3 text-[13.5px] font-semibold text-blue-700 hover:bg-blue-50">
                            {edycja ? 'Gotowe' : 'Popraw'}
                          </button>
                        </div>
                      )}
                      {edycja && (
                        <div className={`flex flex-col gap-2.5 ${nipStan === 'znaleziony' ? 'border-t border-gray-100 pt-3' : ''}`}>
                          <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-gray-700">
                            <span>Nazwa firmy{nipStan === 'brak' && <span className="text-red-700"> *</span>}</span>
                            <input value={firma.name} onChange={(e) => setFirma((f) => ({ ...f, name: e.target.value }))}
                                   aria-invalid={proba && brakNazwy} className={`${malePoleKlasa} ${proba && brakNazwy ? '!border-red-600' : ''}`} />
                          </label>
                          {proba && brakNazwy && <Blad>Wpisz nazwę firmy, rejestr jej nie podał.</Blad>}
                          <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-gray-700">Ulica i numer
                            <input value={firma.street} onChange={(e) => setFirma((f) => ({ ...f, street: e.target.value }))} className={malePoleKlasa} />
                          </label>
                          <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-2.5 sm:grid-cols-[130px_minmax(0,1fr)]">
                            <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-gray-700">Kod pocztowy
                              <input value={firma.postal_code} onChange={(e) => setFirma((f) => ({ ...f, postal_code: e.target.value }))} className={malePoleKlasa} />
                            </label>
                            <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-gray-700">Miejscowość
                              <input value={firma.city} onChange={(e) => setFirma((f) => ({ ...f, city: e.target.value }))} className={malePoleKlasa} />
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex flex-col gap-2.5">
                    <span className="text-[13.5px] font-semibold">Czym zajmuje się firma? <span className="font-normal text-gray-500">Możesz zaznaczyć kilka</span></span>
                    <div className="flex flex-wrap gap-2">
                      {typyKonta.map((t) => (
                        <Chip key={t.value} on={typy.includes(t.value)} onClick={() => przelaczTyp(t.value)} title={opisTypu(t.value)}>{t.label}</Chip>
                      ))}
                    </div>
                    <span className="text-[12.5px] text-gray-500">
                      {ostatni && typy.includes(ostatni.value)
                        ? `${ostatni.label}: ${opisTypu(ostatni.value).charAt(0).toLowerCase()}${opisTypu(ostatni.value).slice(1)}.`
                        : 'Dopasujemy podpowiedzi do tego, jak pracujecie.'}
                    </span>
                    {typy.includes('other') && (
                      <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-gray-700">Opisz krótko
                        <input maxLength={120} placeholder="np. serwis i przeglądy instalacji" value={inne} onChange={(e) => setInne(e.target.value)} className={malePoleKlasa} />
                      </label>
                    )}
                  </div>
                </div>
              )}

              <span className="text-[12.5px] text-gray-500">Wszystko w tej części zmienisz później w ustawieniach konta.</span>
            </div>

            {/* Zgody rozdzielone: regulamin jest warunkiem usługi, marketing musi być dobrowolny. */}
            <div className="flex flex-col gap-3 border-t border-gray-100 pt-[18px]">
              <label className="flex cursor-pointer items-start gap-3 text-sm leading-normal">
                <input type="checkbox" checked={regulamin} onChange={(e) => setRegulamin(e.target.checked)} disabled={busy}
                       className="mt-0.5 h-5 w-5 flex-shrink-0 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span>
                  Akceptuję <Link to="/regulamin" target="_blank" className="text-blue-600 hover:underline">regulamin</Link>
                  {' '}i <Link to="/polityka-prywatnosci" target="_blank" className="text-blue-600 hover:underline">politykę prywatności</Link>
                  <span className="text-red-700"> *</span>
                </span>
              </label>
              {proba && !regulamin && <span role="alert" className="pl-8 text-[13px] text-red-700">Zaznacz akceptację regulaminu, żeby założyć konto.</span>}
              <label className="flex cursor-pointer items-start gap-3 text-sm leading-normal">
                <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} disabled={busy}
                       className="mt-0.5 h-5 w-5 flex-shrink-0 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span>Chcę dostawać informacje o nowościach i promocjach <span className="text-gray-500">(opcjonalnie)</span></span>
              </label>
            </div>

            <div className="flex flex-col gap-3.5">
              <button type="submit" disabled={busy}
                      className="flex h-[52px] w-full items-center justify-center gap-2.5 rounded-xl bg-blue-600 text-base font-semibold text-white shadow-[0_6px_16px_rgba(37,99,235,0.28)] transition hover:bg-blue-700 disabled:cursor-wait disabled:opacity-70">
                {busy ? 'Zakładam konto…' : <>Załóż konto<ArrowRightIcon className="h-[18px] w-[18px]" strokeWidth={2.2} aria-hidden="true" /></>}
              </button>
              <p className="m-0 text-center text-sm text-gray-500">Masz już konto? <Link to="/logowanie" className="font-semibold text-blue-600 hover:text-blue-700">Zaloguj się</Link></p>
            </div>
          </form>
        </section>

        <section aria-labelledby="po-co-konto" className="flex min-w-0 flex-col gap-7 lg:flex-[1_1_380px] lg:pt-2">
          <div className="flex flex-col gap-3.5">
            <span className="inline-flex items-center gap-2 self-start rounded-full bg-blue-50 px-3 py-1.5 text-[13px] font-semibold text-blue-700">
              <CheckIcon className="h-[15px] w-[15px]" strokeWidth={2.4} aria-hidden="true" />Konto jest darmowe
            </span>
            <h1 id="po-co-konto" className="m-0 max-w-[480px] text-[30px] font-bold leading-[1.15] tracking-[-0.02em] sm:text-[40px]">
              {firmowe ? 'Konto firmowe w VoltValue' : 'Dobieraj i porównuj sprzęt PV na swoim koncie'}
            </h1>
            <p className="m-0 max-w-[460px] text-base leading-relaxed text-gray-600">
              {firmowe
                ? 'Zajmie to około minuty. Po potwierdzeniu adresu krótka ankieta firmy i możesz dostawać zlecenia z Rynku.'
                : 'Zajmie to około minuty. Potem wszystko, co zapiszesz, czeka na Ciebie na każdym urządzeniu.'}
            </p>
          </div>
          <ul className="m-0 flex list-none flex-col gap-[18px] p-0">
            {(firmowe ? KORZYSCI_FIRMY : KORZYSCI_KONTA).map(({ Icon, tytul, opis }) => (
              <li key={tytul} className="flex items-start gap-3.5">
                <span aria-hidden="true" className="flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-blue-600">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="flex flex-col gap-0.5">
                  <strong className="text-[15px] font-semibold">{tytul}</strong>
                  <span className="text-sm leading-normal text-gray-500">{opis}</span>
                </span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-x-[22px] gap-y-2.5 border-t border-gray-200 pt-5 text-[13px] text-gray-600">
            <span className="inline-flex items-center gap-2"><CreditCardIcon className="h-[18px] w-[18px]" aria-hidden="true" />Bez karty płatniczej</span>
            <span className="inline-flex items-center gap-2"><ShieldCheckIcon className="h-[18px] w-[18px]" aria-hidden="true" />Dane firmy z Białej listy VAT</span>
            <span className="inline-flex items-center gap-2"><EnvelopeIcon className="h-[18px] w-[18px]" aria-hidden="true" />Newsletter tylko za Twoją zgodą</span>
          </div>
        </section>
      </div>
    </div>
  )
}

/** Ekran po wysłaniu formularza (plansza „Po rejestracji · sprawdź skrzynkę”). */
const Skrzynka = ({ email, firma, maKonto, onPopraw }) => {
  // Firma ma jeszcze jeden krok: bez ankiety nie dostaje zleceń z Rynku (decyzja 2026-10-23).
  const KROKI = [['Konto założone', 'zrobione'], ['Potwierdź e-mail', 'teraz'],
    [firma && !maKonto ? 'Ankieta firmy' : 'Gotowe', 'potem']]
  return (
    <div className="bg-gray-50 text-gray-900">
      <div className="mx-auto flex w-full max-w-[600px] flex-col gap-5 px-4 pb-10 pt-10 sm:px-6 sm:pt-14">
        <section aria-labelledby="tytul-skrzynka"
                 className="flex flex-col items-center gap-6 rounded-[20px] border border-gray-200 bg-white px-5 pb-8 pt-9 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_32px_rgba(15,23,42,0.06)] sm:px-8">
          <ol aria-label="Postęp rejestracji" className="m-0 grid w-full list-none grid-cols-3 gap-2 p-0">
            {KROKI.map(([nazwa, stan]) => (
              <li key={nazwa} aria-current={stan === 'teraz' ? 'step' : undefined} className="flex flex-col items-center gap-2">
                <span className={`h-1 w-full rounded-full ${{ zrobione: 'bg-emerald-600', teraz: 'bg-blue-600', potem: 'bg-gray-200' }[stan]}`} />
                <span className={`inline-flex items-center gap-1.5 text-[12.5px] ${{ zrobione: 'font-semibold text-emerald-700', teraz: 'font-semibold text-blue-700', potem: 'font-medium text-gray-500' }[stan]}`}>
                  {stan === 'zrobione' && <CheckIcon className="h-3.5 w-3.5" strokeWidth={2.6} aria-hidden="true" />}{nazwa}
                </span>
              </li>
            ))}
          </ol>

          <div aria-hidden="true" className="relative flex h-[88px] w-[88px] items-center justify-center rounded-full bg-blue-50 text-blue-600">
            <EnvelopeIcon className="h-10 w-10" strokeWidth={1.5} />
            <span className="absolute bottom-0.5 right-0.5 flex h-7 w-7 items-center justify-center rounded-full border-[3px] border-white bg-emerald-600 text-white">
              <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
            </span>
          </div>

          <div className="flex flex-col items-center gap-2.5">
            <h1 id="tytul-skrzynka" className="m-0 text-[28px] font-bold tracking-[-0.01em]">Sprawdź swoją skrzynkę</h1>
            <p className="m-0 max-w-[420px] text-[15.5px] leading-relaxed text-gray-600">
              Wysłaliśmy link na <strong className="font-semibold text-gray-900">{email}</strong>. Kliknij go, żeby dokończyć rejestrację.
            </p>
          </div>

          <div className="grid w-full grid-cols-1 gap-2.5 sm:grid-cols-2">
            {[['Otwórz Gmail', 'https://mail.google.com'], ['Otwórz Outlook', 'https://outlook.live.com']].map(([nazwa, href]) => (
              <a key={nazwa} href={href} target="_blank" rel="noopener noreferrer"
                 className="flex h-[50px] items-center justify-center gap-2 rounded-xl border-[1.5px] border-gray-300 bg-white text-[15px] font-semibold text-gray-900 no-underline hover:border-gray-400">
                {nazwa}<ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
              </a>
            ))}
          </div>

          <span className="text-[13.5px] text-gray-500">
            Zły adres? <button type="button" onClick={onPopraw} className="font-semibold text-blue-600 hover:text-blue-700">Popraw e-mail</button>
          </span>

          {/* Firma podpina się dopiero po potwierdzeniu adresu (accounts/signals.py); bez tego zdania wyglądałoby
              to na zgubione dane. */}
          {firma && (
            <div className="flex w-full items-start gap-3 rounded-[14px] border border-gray-200 bg-gray-50 px-4 py-3.5 text-left">
              <span aria-hidden="true" className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-blue-50 text-blue-600">
                <BuildingOffice2Icon className="h-[18px] w-[18px]" />
              </span>
              {maKonto ? (
                <span className="text-[13.5px] leading-normal text-gray-700">
                  {firma.name && <strong className="font-semibold text-gray-900">{firma.name} </strong>}(NIP {firma.nip}): {TEKST_FIRMA_MA_KONTO}
                </span>
              ) : (
                <span className="text-[13.5px] leading-normal text-gray-700">
                  Dane firmy {firma.name && <strong className="font-semibold text-gray-900">{firma.name}</strong>} (NIP {firma.nip}) przypiszemy
                  do konta po potwierdzeniu adresu. Sprawdzimy je wtedy w rejestrze na nowo. Po zalogowaniu wypełnij ankietę
                  firmy (2 minuty), żeby dostawać zlecenia z Rynku.
                </span>
              )}
            </div>
          )}
        </section>

        <section aria-labelledby="nie-dotarla" className="flex flex-col gap-2.5 rounded-2xl border border-gray-200 bg-white px-6 py-5">
          <h2 id="nie-dotarla" className="m-0 text-[15px] font-semibold">Wiadomość nie dotarła?</h2>
          <ul className="m-0 flex list-none flex-col gap-2 p-0 text-sm text-gray-600">
            {[
              'Zajrzyj do folderu Spam albo Oferty.',
              'Link jest ważny 3 dni.',
              <>Dalej nic? Napisz do nas na <a href="mailto:kontakt@voltvalue.pl" className="text-blue-600 hover:underline">kontakt@voltvalue.pl</a>.</>,
            ].map((tekst, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-600" />{tekst}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  )
}

export default Signup
